import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import dynamicfunction from '../../../utils/dynamicfunction';
import { getContentKeyAccToLang, showToast } from '../../../utils/utils';
import {
  getClientClassification,
  getClientClassificationDropdowns,
  getKycClientClassificationData,
  saveClientClassification,
  saveCorpDocumentClientClassification,
  saveCorpKycClientInfo,
} from '../../../store/actions/corporateAction';
import { client_classification_validation_schema } from '../../../validations_schemas/client_classification_schema';
import { Checkbox, ErrorText, RowView, Text } from '../components';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '../../../components/Button';
import CustomInput from '../../../components/CustomInput';
import { logoutUser } from '../../../store/actions/authActions';
const ClientClassification = ({ onDone }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isArabic } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const { corporate_post_loading, client_classification_dropdowns, client_classification_data } = useSelector(
    (state) => state.corporate
  );
  const {
    client_application_id,
    is_kyc,
  } = useSelector((state) => state.constant);

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_client_classification?.data?.attributes, key, isArabic);
  };

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getClientClassificationDropdowns());
    await dispatch(is_kyc ? getKycClientClassificationData(client_application_id) : getClientClassification(client_application_id));
  }

  const onSubmit = (values) => {
    const entityCriteriasSpecial = values?.clientClassificationAnyEntityCriterias?.some((item) => item?.valueId === 1671);
    const entityCriteriasLength = entityCriteriasSpecial ? 0 : (values?.clientClassificationAnyEntityCriterias?.length ?? 0);

    const legalPersonCriteriasSpecial = values?.clientClassificationAnyLegalPersonCriterias?.some((item) => item?.valueId === 1536);
    const legalPersonCriteriasLength = legalPersonCriteriasSpecial ? 0 : (values?.clientClassificationAnyLegalPersonCriterias?.length ?? 0);

    const checkDocument = entityCriteriasLength + legalPersonCriteriasLength === values?.documentsList?.length;

    if (!checkDocument) {
      showToast({
        icon: 'error',
        title: getContent('please_upload_all_documents'),
      });
      return;
    }

    const api_data = {
      corpClientApplicationId: client_application_id,
      othersPleaseSpecify: values?.othersPleaseSpecify,
      clientClassificationAnyEntityCriterias: values?.clientClassificationAnyEntityCriterias,
      clientClassificationAnyLegalPersonCriterias: values?.clientClassificationAnyLegalPersonCriterias,
    };
    dispatch(
      is_kyc ?
        saveCorpKycClientInfo(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        }) :
        saveClientClassification(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };
  const onDeleteDocument = (option, values, setFieldValue) => {
    let array = [...values?.documentsList];
    setFieldValue(
      'documentsList',
      array?.filter((val) => val?.investmentCriteriaId?.toString() !== option?.id?.toString()),
    );
  };

  const uploadDocument = (option, setFieldValue, values) => {
    const selectedId = option?.id?.toString();

    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.png,.jpg,.jpeg';
      input.style.display = 'none';

      input.onchange = (event) => {
        const file = event.target.files?.[0];

        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        const apiData = {
          params: `?corpClientApplicationId=${client_application_id}&selectionItemId=${selectedId}`,
          data: formData,
        };

        dispatch(
          saveCorpDocumentClientClassification(apiData, (res) => {
            let array = values?.documentsList?.length > 0 ? [...values?.documentsList] : [];

            const documentIndex = array?.findIndex((val) => val?.investmentCriteriaId === selectedId);

            if (documentIndex !== -1) {
              array[documentIndex] = {
                ...array[documentIndex],
                documentName: file?.name,
                documentMimeType: file?.type,
              };
            } else {
              array.push({
                investmentCriteriaId: selectedId,
                documentName: file?.name,
                documentMimeType: file?.type,
              });
            }
            setFieldValue('documentsList', array);
          }),
        );
      };

      document.body.appendChild(input);
      input.click();
      document.body.removeChild(input);
    } catch (error) { }
  };

  const removeDocumentsByCriteria = (documents, criteriaList) => {
    const ids = criteriaList.map((c) => c.valueId?.toString());
    return documents.filter((doc) => !ids.includes(doc?.investmentCriteriaId?.toString()));
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          othersPleaseSpecify: client_classification_data?.othersPleaseSpecify ?? '',
          clientClassificationAnyEntityCriterias: client_classification_data?.clientClassificationAnyEntityCriterias ?? [],
          clientClassificationAnyLegalPersonCriterias: client_classification_data?.clientClassificationAnyLegalPersonCriterias ?? [],
          isSaveExist: false,
          documentsList: client_classification_data?.documents ?? [],
        }}
        validationSchema={client_classification_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, errors, touched, setFieldTouched }) => {
          return (
            <div className=''>
              <div>
                <Checkbox
                  label={getContent('applicable_conditions')}
                  value={values?.clientClassificationAnyEntityCriterias}
                  onChange={(option) => {
                    const SPECIAL_ID = 1671;

                    let updated_arr = [...(values?.clientClassificationAnyEntityCriterias || [])];
                    let updatedDocuments = [...(values?.documentsList || [])];

                    const exists = updated_arr.some((item) => item?.valueId === option?.id);

                    // ❌ If already selected → remove it
                    if (exists) {
                      updated_arr = updated_arr.filter((item) => item?.valueId !== option?.id);

                      updatedDocuments = updatedDocuments.filter((doc) => doc?.investmentCriteriaId?.toString() !== option?.id?.toString());
                    } else {
                      // ✅ If selecting 1671 → remove all others
                      if (option?.id === SPECIAL_ID) {
                        updated_arr = [
                          {
                            typeOfEntityCriteriaId: 0,
                            valueId: SPECIAL_ID,
                          },
                        ];

                        updatedDocuments = updatedDocuments.filter((doc) => doc?.investmentCriteriaId?.toString() === SPECIAL_ID.toString());
                      } else {
                        // ✅ If selecting any other → remove 1671
                        updated_arr = updated_arr.filter((item) => item?.valueId !== SPECIAL_ID);

                        updated_arr.push({
                          typeOfEntityCriteriaId: 0,
                          valueId: option?.id,
                        });

                        updatedDocuments = updatedDocuments.filter((doc) => doc?.investmentCriteriaId?.toString() !== SPECIAL_ID.toString());
                      }
                    }

                    setFieldValue('clientClassificationAnyEntityCriterias', updated_arr);
                    setFieldTouched('clientClassificationAnyEntityCriterias', false);
                    setFieldValue('documentsList', updatedDocuments);
                  }}
                  options={client_classification_dropdowns?.doesAnyOfTheFollowingApplyToYou}
                  isVertical
                  isUploadDocument={!values?.clientClassificationAnyEntityCriterias?.some((item) => item?.valueId === 1671)}
                  uploadedDocuments={values?.documentsList}
                  onPressDocument={(option) => uploadDocument(option, setFieldValue, values)}
                  onDeleteDocument={(option) => onDeleteDocument(option, values, setFieldValue)}
                  getContent={getContent}
                />
                <ErrorText text={getContent(touched?.clientClassificationAnyEntityCriterias ? errors?.clientClassificationAnyEntityCriterias : '')} className='block' />
              </div>

              <div className='pt-3 sm:pt-5'>
                <Checkbox
                  label={getContent('legal_person_own_account_conditions')}
                  value={values?.clientClassificationAnyLegalPersonCriterias}
                  onChange={(option) => {
                    const SPECIAL_ID = 1536;

                    let updated_arr = [...(values?.clientClassificationAnyLegalPersonCriterias || [])];
                    let updatedDocuments = [...(values?.documentsList || [])];

                    const exists = updated_arr?.some((item) => item?.valueId === option?.id);

                    // ❌ If already selected → remove it
                    if (exists) {
                      updated_arr = updated_arr?.filter((item) => item?.valueId !== option?.id);

                      updatedDocuments = updatedDocuments?.filter((doc) => doc?.investmentCriteriaId?.toString() !== option?.id?.toString());
                    } else {
                      // ✅ If selecting 1536 → remove all others
                      if (option?.id === SPECIAL_ID) {
                        updated_arr = [
                          {
                            typeOfLegalPersonCriteriaId: 0,
                            valueId: SPECIAL_ID,
                          },
                        ];

                        updatedDocuments = removeDocumentsByCriteria(updatedDocuments, values?.clientClassificationAnyLegalPersonCriterias || []);
                      } else {
                        // ✅ If selecting any other → remove 1671
                        updated_arr = updated_arr?.filter((item) => item?.valueId !== SPECIAL_ID);

                        updated_arr.push({
                          typeOfLegalPersonCriteriaId: 0,
                          valueId: option?.id,
                        });

                        updatedDocuments = updatedDocuments?.filter((doc) => doc?.investmentCriteriaId?.toString() !== SPECIAL_ID.toString());
                      }
                    }

                    setFieldValue('clientClassificationAnyLegalPersonCriterias', updated_arr);
                    setFieldTouched('clientClassificationAnyLegalPersonCriterias', false);
                    setFieldValue('documentsList', updatedDocuments);
                  }}
                  options={client_classification_dropdowns?.anyLegalPersonActingForItsOwnAccountAndBeAnyOfTheFollowing}
                  isVertical
                  isUploadDocument={!values?.clientClassificationAnyLegalPersonCriterias?.some((item) => item?.valueId === 1536)}
                  uploadedDocuments={values?.documentsList}
                  onPressDocument={(option) => uploadDocument(option, setFieldValue, values)}
                  onDeleteDocument={(option) => onDeleteDocument(option, values, setFieldValue)}
                  getContent={getContent}
                />
                <ErrorText
                  text={getContent(touched?.clientClassificationAnyLegalPersonCriterias ? errors?.clientClassificationAnyLegalPersonCriterias : '')} className='block'
                />
              </div>
              <div className='pt-3 sm:pt-5'>
                <RowView className={'gap-1 items-center'}>
                  <ErrorOutlineIcon sx={{ color: 'red' }} />
                  <Text text={getContent('supporting_information_required')} color='text-red' isTruncate={false} />
                </RowView>
              </div>
              <RowView>
                <RowView className='pt-3 sm:pt-5 w-full'>
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('others_Specify')}
                    value={values?.othersPleaseSpecify}
                    onChange={(e) => {
                      setFieldValue('othersPleaseSpecify', e.target.value);
                      setFieldTouched('othersPleaseSpecify', true, false);
                    }}
                    placeholder={getContent('placeholder_others_Specify')}
                    errorMessage={getContent(touched?.othersPleaseSpecify ? errors?.othersPleaseSpecify : '')}
                  />
                </RowView>
              </RowView>
              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  loading={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default ClientClassification;
